import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';

const CATEGORIES = [
  { value: 'promo', label: 'ALS Promo / Branding', emoji: '🌟' },
  { value: 'event', label: 'Event Atmosphere', emoji: '✨' },
  { value: 'venue', label: 'The Venue', emoji: '🏛️' },
  { value: 'dresscode', label: 'Dress Code', emoji: '👔' }
];

export default function AdminGallery() {
  const [images, setImages] = useState([]);
  const [category, setCategory] = useState('promo');
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [filterCat, setFilterCat] = useState('all');
  const fileRef = useRef();

  const load = () => api.get('/gallery').then(r => setImages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    setUploading(true);
    setMsg('');
    try {
      const fd = new FormData();
      imageFiles.forEach(f => fd.append('images', f));
      fd.append('category', category);
      const res = await api.post('/gallery/upload', fd);
      setMsg(`✓ ${res.data.images.length} image(s) uploaded`);
      load();
    } catch {
      setMsg('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    await api.delete(`/gallery/${id}`);
    setImages(imgs => imgs.filter(i => i._id !== id));
  };

  const handleCategoryChange = async (id, newCat) => {
    await api.put(`/gallery/${id}`, { category: newCat });
    setImages(imgs => imgs.map(i => i._id === id ? { ...i, category: newCat } : i));
  };

  const displayImages = filterCat === 'all' ? images : images.filter(i => i.category === filterCat);

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Event Gallery</h1>
        <p className="admin-page-sub">{images.length} images uploaded across all categories</p>
      </div>

      {/* Upload Zone */}
      <div className="admin-card">
        <div className="admin-card-title">Upload Photos</div>
        <div className="admin-field">
          <label className="admin-label">Category</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {CATEGORIES.map(c => (
              <button
                key={c.value}
                onClick={() => setCategory(c.value)}
                style={{
                  padding: '8px 16px', borderRadius: 10, border: '1px solid',
                  fontSize: 13, cursor: 'pointer', transition: 'all 0.2s',
                  borderColor: category === c.value ? '#d4af37' : 'rgba(255,255,255,0.1)',
                  background: category === c.value ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.03)',
                  color: category === c.value ? '#d4af37' : 'rgba(255,255,255,0.5)'
                }}
              >
                {c.emoji} {c.label}
              </button>
            ))}
          </div>
        </div>

        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current.click()}
          style={{
            border: `2px dashed ${dragOver ? '#d4af37' : 'rgba(212,175,55,0.25)'}`,
            borderRadius: 16, padding: '36px 24px', textAlign: 'center', cursor: 'pointer',
            background: dragOver ? 'rgba(212,175,55,0.08)' : 'rgba(212,175,55,0.03)',
            transition: 'all 0.2s'
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 10 }}>{uploading ? '⏳' : '📸'}</div>
          <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>
            {uploading ? 'Uploading...' : <><strong style={{ color: '#d4af37' }}>Click or drag & drop</strong> photos here</>}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
            PNG, JPG, WEBP · Max 20MB each · Multiple files supported
          </div>
        </div>
        <input ref={fileRef} type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
        {msg && <p style={{ marginTop: 10, fontSize: 13, color: msg.startsWith('✓') ? '#4ade80' : '#f87171' }}>{msg}</p>}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          className={`admin-btn ${filterCat === 'all' ? '' : 'admin-btn-ghost'}`}
          style={{ padding: '7px 16px', fontSize: 13 }}
          onClick={() => setFilterCat('all')}
        >
          All ({images.length})
        </button>
        {CATEGORIES.map(c => {
          const count = images.filter(i => i.category === c.value).length;
          return (
            <button
              key={c.value}
              className={`admin-btn ${filterCat === c.value ? '' : 'admin-btn-ghost'}`}
              style={{ padding: '7px 16px', fontSize: 13 }}
              onClick={() => setFilterCat(c.value)}
            >
              {c.emoji} {c.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Image Grid */}
      {displayImages.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'rgba(255,255,255,0.2)', fontSize: 14 }}>
          No images in this category yet. Upload some above.
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 12
        }}>
          {displayImages.map(img => (
            <div key={img._id} style={{
              position: 'relative', borderRadius: 12, overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(255,255,255,0.02)'
            }}>
              <img
                src={`/uploads/gallery/${img.filename}`}
                alt={img.caption || 'Gallery'}
                style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
              />
              <div style={{ padding: '8px 10px' }}>
                <select
                  value={img.category}
                  onChange={e => handleCategoryChange(img._id, e.target.value)}
                  style={{
                    width: '100%', background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                    padding: '4px 8px', color: '#d4af37', fontSize: 11, cursor: 'pointer', marginBottom: 6
                  }}
                >
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>)}
                </select>
                <button
                  onClick={() => handleDelete(img._id)}
                  style={{
                    width: '100%', padding: '5px', background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6,
                    color: '#f87171', fontSize: 12, cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
